import { blobToBase64 } from './blob'
import { FaceMatchValidateState, Mobbscan, MobbscanErrorCode, MobbscanFaceMatchState, MobbscanScaningState, MobbscanScannedResult, MobbscanSource } from './mobbscan'
import type { MobbscanDocument, ScanSideType } from './mobbscan'
import { isUnreadable, mobbscanDocumentToGuest } from './reading'
import { CaptureError } from './types'
import type { DocumentCapture, DocumentEvent, FaceEvent, Side } from './types'

const sideType = (d: { both: boolean; front: boolean }): ScanSideType => (d.both ? 'SCAN_BOTH_SIDES' : d.front ? 'SCAN_ONLY_FRONT' : 'SCAN_ONLY_BACK')

/**
 * Captura con MobbScan. Mismo protocolo que AutoCollectScan de la app actual: init + start, eventos
 * STARTED/DETECTED por lado, `scan(front, back)` y `validate(FACE)`. Diferencia de orden (inventario D32):
 * el diseño pide revisar los datos (3b) antes de la foto de la cara (4), así que la cara se captura
 * después de `scan()` con `hideDocumentsAndShowFace()`; hoy se captura antes y se valida después.
 */
export function createMobbscanCapture(): DocumentCapture {
  let onDoc: ((e: DocumentEvent) => void) | null = null
  let onFace: ((e: FaceEvent) => void) | null = null
  let needed: Side[] = []
  const images: { front?: Blob; back?: Blob; face?: Blob } = {}

  const listener = ({ state, data, error }: { state: string; data: { image: Blob; side: 'FRONT' | 'BACK'; source: string }; error: { code?: string } | null }) => {
    if (state === MobbscanScaningState.Error || state === MobbscanScaningState.Timeout) {
      if (error?.code === MobbscanErrorCode.WebcamDenied) onDoc?.({ type: 'error', code: 'no_camera' })
      else if (error?.code === MobbscanErrorCode.NoWebcamFound) onDoc?.({ type: 'error', code: 'camera_unavailable' })
      else if (error?.code === MobbscanErrorCode.ConnectionError) onDoc?.({ type: 'error', code: 'connection' })
      else {
        // Como hoy: vuelve a mostrar las dos caras y deja repetir.
        Mobbscan.showMobbscanFront()
        Mobbscan.showMobbscanBack()
        onDoc?.({ type: 'ready' })
      }
      return
    }
    if (state === MobbscanScaningState.NotDetected && data?.source === MobbscanSource.File) {
      onDoc?.({ type: 'error', code: 'unreadable' })
      return
    }
    if (state === MobbscanScaningState.Started) {
      if (data.side === 'FRONT') Mobbscan.hideMobbscanBack()
      else Mobbscan.hideMobbscanFront()
      onDoc?.({ type: 'capturing', side: data.side === 'FRONT' ? 'front' : 'back' })
      return
    }
    if (state === MobbscanScaningState.Detected) {
      const side: Side = data.side === 'FRONT' ? 'front' : 'back'
      images[side] = data.image
      // Como el legacy, conservamos este lado visible hasta que la persona confirme la foto.
      onDoc?.({ type: 'captured', side, fromFile: data.source === MobbscanSource.File, image: data.image })
    }
  }

  const faceListener = (state: string, data: { image: Blob }) => {
    if (state === MobbscanFaceMatchState.Started) onFace?.({ type: 'capturing' })
    else if (state === MobbscanFaceMatchState.Detected && data?.image) {
      images.face = data.image
      onFace?.({ type: 'captured' })
    }
  }

  return {
    startDocument({ containerId, documentType }, onEvent) {
      onDoc = onEvent
      needed = documentType.both ? ['front', 'back'] : documentType.front ? ['front'] : ['back']
      delete images.front
      delete images.back
      if (window.isSecureContext === false) return onEvent({ type: 'error', code: 'insecure' })
      if (!Mobbscan.isLoaded()) return onEvent({ type: 'error', code: 'connection' })
      try {
        Mobbscan.init({ placeholderId: containerId, listener, faceMatchListener: faceListener })
        if (document.querySelector(`#${containerId}`)) {
          Mobbscan.start({ scanCode: documentType.scanCode ?? '', scanSideType: sideType(documentType) })
          // El SDK dibuja ambos lados inicialmente. En la v2 solo mostramos el que toca capturar.
          if (needed[0] === 'front') Mobbscan.hideMobbscanBack()
          else Mobbscan.hideMobbscanFront()
        }
        onEvent({ type: 'ready' })
      } catch {
        onEvent({ type: 'error', code: 'connection' })
      }
    },
    capture(side) {
      if (side === 'front') Mobbscan.initFrontCapture()
      else Mobbscan.initBackCapture()
    },
    showSide(side) {
      // El SDK conserva la última foto en ambos paneles. Nunca deben coexistir:
      // al pedir una cara mostramos solo su panel y ocultamos el de la otra.
      if (side === 'front') {
        Mobbscan.hideMobbscanBack()
        Mobbscan.showMobbscanFront()
      } else {
        Mobbscan.hideMobbscanFront()
        Mobbscan.showMobbscanBack()
      }
    },
    pickFile(side) {
      // D9: el propio SDK abre su selector de archivo desde la miniatura del lado.
      if (side === 'front') Mobbscan.initFrontCapture()
      else Mobbscan.initBackCapture()
    },
    giveFile(side, file) {
      const input = document.querySelector<HTMLInputElement>(`.msw-div-${side === 'front' ? 'FRONT' : 'BACK'} input[type="file"]`) ?? document.querySelector<HTMLInputElement>('#mobbscan-container input[type="file"]')
      if (!input || typeof DataTransfer === 'undefined') return false
      const dt = new DataTransfer()
      dt.items.add(file)
      input.files = dt.files
      input.dispatchEvent(new Event('change', { bubbles: true }))
      return true
    },
    missingSides: () => needed.filter((s) => !images[s]),
    read(ctx) {
      const front = images.front ?? images.back
      if (!front) return Promise.reject(new CaptureError('unreadable', 'no image'))
      return new Promise((resolve, reject) => {
        Mobbscan.scan({
          frontImage: front,
          backImage: images.front ? images.back : undefined,
          onScan: () => undefined,
          onScanned: ({ result, data }) => {
            const doc: Partial<MobbscanDocument> = data?.document ?? {}
            if (result === MobbscanScannedResult.Error || isUnreadable(doc)) return reject(new CaptureError('unreadable'))
            void (async () => {
              const { guest, doubtful } = await mobbscanDocumentToGuest(doc, ctx)
              const attachments = {
                ...(images.front ? { front: await blobToBase64(images.front) } : {}),
                ...(images.back ? { back: await blobToBase64(images.back) } : {}),
              }
              resolve({ guest, doubtful, attachments, rawData: doc })
            })().catch(reject)
          },
        })
      })
    },
    startFace(onEvent) {
      onFace = onEvent
      delete images.face
      try {
        // Normalmente el documento ya inicializó el SDK. Si se llega directamente a
        // este paso (depuración) o MobScan perdió su panel al moverlo entre rutas,
        // inicializamos el placeholder para que pueda construir el visor facial.
        if (!document.querySelector('#mobbscan-container #msw-div-face')) {
          Mobbscan.init({ placeholderId: 'mobbscan-container', listener, faceMatchListener: faceListener })
        }
        Mobbscan.hideDocumentsAndShowFace()
      } catch {
        onEvent({ type: 'error', code: 'connection' })
      }
    },
    captureFace() {
      // El contenedor se mueve entre los pasos de la v2. Reafirmamos el panel facial
      // antes del clic interno del SDK para que el vídeo se inicie en el visor visible.
      Mobbscan.hideDocumentsAndShowFace()
      Mobbscan.initFaceMatchCapture()
    },
    validateFace() {
      const image = images.face
      if (!image) return Promise.reject(new CaptureError('unreadable', 'no face'))
      return new Promise((resolve, reject) => {
        Mobbscan.validateFaceMatch({
          image,
          callback: (result, resultData) => {
            if (result !== MobbscanScannedResult.Ok) return reject(new CaptureError('connection'))
            void blobToBase64(image)
              .then((face) => resolve({ valid: resultData?.data?.state !== FaceMatchValidateState.NotValid, face }))
              .catch(reject)
          },
        })
      })
    },
    stop() {
      onDoc = null
      onFace = null
    },
  }
}
